import * as React from 'react';
import { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import lowerCase from 'lodash/lowerCase';
import type { MultiValueGenericProps, MultiValueRemoveProps } from 'react-select';
import { components } from 'react-select';

import PaginatedSelect from 'components/common/Select/PaginatedSelect';
import { fetchPaginatedCollections } from 'collections/hooks/useCollections';
import { Input } from 'components/bootstrap';
import type { EntitySharePayload } from 'actions/permissions/EntityShareActions';
import { createGRN } from 'logic/permissions/GRN';
import type { Collection } from 'collections/types';
import type { SearchParams } from 'stores/PaginationTypes';
import { Spinner, Tooltip } from 'components/common';
import useCurrentUser from 'hooks/useCurrentUser';
import useUserDefaultCollectionsOptions from 'collections/hooks/useUserDefaultCollectionsOptions';
import { COLLECTION_EXCLUDED_ENTITY_TYPES } from 'collections/constants';

type Props = {
  label?: React.ReactElement | string;
  name?: string;
  entityType?: string;
  error?: any;
  hideHelp?: boolean;
  filterValueFromOption?: boolean;
  value?: Array<string>;
  overrideExclude?: boolean;
  onChange: (values: Pick<EntitySharePayload, 'selected_collections'>) => void;
};

export const StyledLabel = styled.span(
  ({ theme }) => css`
    font-size: ${theme.fonts.size.h5};
  `,
);

const MultiValueLabel = (props: MultiValueGenericProps<any>) => (
  <Tooltip label="This collection is assigned from teams membership" disabled={!props.data.isFixed}>
    <span>
      <components.MultiValueLabel {...props} />
    </span>
  </Tooltip>
);
const MultiValueRemove = (props: MultiValueRemoveProps<any>) => {
  if (props.data?.isFixed) {
    return null;
  }

  return <components.MultiValueRemove {...props} />;
};

const formatCollections = (collections: Array<Collection>) =>
  collections?.map((collection) => ({
    label: `${collection.title}`,
    value: createGRN('collection', collection.id),
    isDefault: false,
  }));

const defaultSearchOptions = {
  page: 1,
  pageSize: 10,
  query: '',
  sort: {
    attributeId: 'title',
    direction: 'asc',
  },
} as SearchParams;

const AddCollectionFormGroup = ({
  error = null,
  label = 'Add to collections',
  entityType = 'entity',
  name = 'collections',
  hideHelp = false,
  filterValueFromOption = false,
  overrideExclude = false,
  value = [],
  onChange,
}: Props) => {
  const user = useCurrentUser();
  const { defaultCollections, removeDefaultCollections, defaultCollectionValue, isLoadingDefaultCollections } =
    useUserDefaultCollectionsOptions(user.id);

  const filterCollections = useCallback(
    (collectionOptions: Array<{ label: string; value: string }>) =>
      filterValueFromOption ? collectionOptions.filter((c) => !value.includes(c.value)) : collectionOptions,
    [filterValueFromOption, value],
  );
  const loadCollections = useCallback(
    (pagination: { page: number; perPage: number; query: string }) =>
      fetchPaginatedCollections(
        {
          ...defaultSearchOptions,
          page: pagination.page,
          pageSize: pagination.perPage,
          query: pagination.query,
        },
        true,
      ).then((results) => ({
        total: results.pagination.total as number,
        list: [
          ...filterCollections(removeDefaultCollections(formatCollections(results.list as Array<Collection>))),
          ...defaultCollections,
        ],
        pagination,
      })),
    [filterCollections, defaultCollections, removeDefaultCollections],
  );

  const handleChange = (values: string, actionMeta) => {
    switch (actionMeta.action) {
      case 'remove-value':
      case 'pop-value':
        if (actionMeta.removedValue.isFixed) {
          return;
        }
        break;
      default:
        break;
    }

    onChange({ selected_collections: values.split(',') ?? [] });
  };

  const defaultValue = useMemo(
    () => (!filterValueFromOption ? [...value, ...defaultCollectionValue].join(',') : ''),
    [filterValueFromOption, defaultCollectionValue, value],
  );

  if (COLLECTION_EXCLUDED_ENTITY_TYPES.includes(entityType) && !overrideExclude) return null;

  if (isLoadingDefaultCollections) {
    return <Spinner />;
  }

  return (
    <Input
      label={<StyledLabel>{label}</StyledLabel>}
      help={!hideHelp ? `Select collections to which this ${lowerCase(entityType)} should be added.` : null}
      formGroupClassName=""
      id={name}
      error={error || undefined}>
      <PaginatedSelect
        id="collections-select-list"
        value={defaultValue}
        placeholder="Select collections(s)..."
        onLoadOptions={loadCollections}
        components={{ MultiValueLabel, MultiValueRemove }}
        multi
        clearable={false}
        onChange={handleChange}
      />
    </Input>
  );
};

export default AddCollectionFormGroup;
